import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import { RequestStatus, useApi } from "@core-ui/hooks/useApi";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import BranchUpdaterService, {
	type OnBranchUpdateListener,
} from "@ext/git/actions/Branch/BranchUpdaterService/logic/BranchUpdaterService";
import OnBranchUpdateCaller from "@ext/git/actions/Branch/BranchUpdaterService/model/OnBranchUpdateCaller";
import { useIsRevision } from "@ext/git/actions/Revisions/logic/hooks/useIsRevision";
import SyncService, { type SyncServiceEvents } from "@ext/git/actions/Sync/logic/SyncService";
import { type DependencyList, useCallback, useEffect, useRef } from "react";
import { type AuthoredCommentsByAuthor, setComments } from "./CommentsStore";

interface CommentsCounterProviderProps {
	children: React.ReactNode;
	deps?: DependencyList;
}

const CommentsCounterProvider = ({ children, deps = [] }: CommentsCounterProviderProps) => {
	const { isNext, isStatic, isStaticCli } = usePlatform();
	const { isReadOnly } = PageDataContextService.value.conf;
	const isRevision = useIsRevision();
	const skip = isReadOnly || isNext || isStatic || isStaticCli || isRevision;

	const { call, status, reset } = useApi<AuthoredCommentsByAuthor>({
		url: (api) => api.getCommentsByAuthors(),
		parse: "json",
		onDone: (data) => setComments(data || {}),
	});

	// `useApi` drops a call made while one is in flight. Since a checkout no longer waits for the recount, a second
	// quick checkout lands in the first one's recount and the counter kept the first branch's number; a recount asked
	// for meanwhile runs once the current one settles.
	const isLoading = useRef(false);
	isLoading.current = status === RequestStatus.Loading;
	const recountAgain = useRef(false);

	const refreshComments = useCallback(async () => {
		if (skip) return;
		if (isLoading.current) {
			recountAgain.current = true;
			return;
		}
		await call();
	}, [call, skip]);

	useEffect(() => {
		if (status === RequestStatus.Loading || !recountAgain.current) return;
		recountAgain.current = false;
		void refreshComments();
	}, [status, refreshComments]);

	// Not returned: `updateBranch` and sync "finish" await their listeners, and a recount walks every
	// article — on a large catalog the branch panel stayed on "Loading" for seconds after a checkout.
	useEffect(() => {
		const onBranchUpdate: OnBranchUpdateListener = (_, caller) => {
			if (
				caller !== OnBranchUpdateCaller.Checkout &&
				caller !== OnBranchUpdateCaller.CheckoutToNewCreatedBranch
			) {
				return;
			}
			void refreshComments();
		};
		const onSyncFinish: SyncServiceEvents["finish"] = ({ syncData }) => {
			if (!syncData.isVersionChanged) return;
			void refreshComments();
		};

		BranchUpdaterService.addListener(onBranchUpdate);
		const syncToken = SyncService.events.on("finish", onSyncFinish);

		return () => {
			BranchUpdaterService.removeListener(onBranchUpdate);
			SyncService.events.off(syncToken);
		};
	}, [refreshComments]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: only deps trigger refetch; status/reset/call are read but must not retrigger
	useEffect(() => {
		if (skip) return;
		if (status !== RequestStatus.Init) return;
		reset();
		void call();
	}, [skip, ...deps]);

	return children;
};

export default CommentsCounterProvider;
